/**
 * When the payment page should ask again, and when it should stop asking.
 *
 * Split out of the page and unit-tested because the failure modes here are all
 * silent. A loop that stops too early leaves a buyer staring at a QR they have
 * already paid; a loop that never stops turns every abandoned tab into steady
 * traffic against Altura's shared rate limit — one Kanata shares with Arutala.
 * Neither looks like a bug from the outside, which is exactly why the rules are
 * written down as a pure function with tests rather than as three conditions
 * inside a `useEffect`.
 *
 * **This is a UX path, not a correctness path.** Whatever this loop concludes,
 * the money is confirmed by the reconcile poller on the API side (60s) and
 * eventually by Altura's merchant callback. If polling fails completely the
 * order is still marked paid — the buyer simply does not watch it happen. So
 * nothing here retries as if it were the source of truth, and nothing here
 * decides anything except when to ask again.
 */

/** The four states the API reports. Altura's own vocabulary stops server-side. */
export type PaymentState = "pending" | "paid" | "failed" | "expired";

/** Why the loop stopped. The page renders a different thing for each. */
export type PollStop =
  | "settled" // paid or failed — there is nothing left to watch
  | "expired" // the QR is dead; the buyer needs "start over"
  | "hidden" // the tab is backgrounded; resumes on visibility
  | "gave-up"; // no expiry known and we have watched long enough

export interface PollPlan {
  /** Milliseconds until the next poll, or null when the loop should stop. */
  delayMs: number | null;
  /** Set when delayMs is null. */
  stop?: PollStop;
}

export interface PollInput {
  state: PaymentState;
  /** Consecutive failed requests. Resets to 0 on any success. */
  consecutiveErrors: number;
  /** Whether the tab is currently backgrounded. */
  hidden: boolean;
  /** Now, in epoch milliseconds. */
  now: number;
  /** Charge expiry in epoch milliseconds, or null when Altura did not say. */
  expiresAt: number | null;
  /** When this page started polling, in epoch milliseconds. */
  startedAt: number;
}

/**
 * The happy-path interval. The ticket asks for 3–5 seconds; 4 is the middle of
 * it. Faster buys nothing — a QRIS settlement takes seconds to reach Altura and
 * the buyer is looking at their banking app, not at us.
 */
export const BASE_INTERVAL_MS = 4_000;

/** The ceiling on backoff. Beyond a minute the page is no longer "live". */
export const MAX_INTERVAL_MS = 60_000;

/**
 * How long to keep watching when no expiry is known.
 *
 * Without an expiry there is no natural stopping point, and "poll forever"
 * against a shared rate limit is a slow denial of service that nobody would
 * notice until it mattered. Half an hour is far longer than any QRIS payment
 * takes and short enough to bound an abandoned tab; after it, the page offers a
 * manual re-check instead.
 */
export const NO_EXPIRY_CAP_MS = 30 * 60_000;

/**
 * The floor on a clamped interval. Without it, a poll scheduled a few
 * milliseconds before expiry would spin.
 */
const MIN_INTERVAL_MS = 1_000;

export function planNextPoll(input: PollInput): PollPlan {
  const { state, consecutiveErrors, hidden, now, expiresAt, startedAt } = input;

  // Terminal first, and before the visibility check: a buyer who paid and then
  // switched apps should come back to a page that already knows.
  if (state === "paid" || state === "failed") return { delayMs: null, stop: "settled" };
  if (state === "expired") return { delayMs: null, stop: "expired" };

  // The QR is dead even if the last response said otherwise — our clock is
  // enough to stop asking. The next render shows the expired state.
  if (expiresAt !== null && now >= expiresAt) return { delayMs: null, stop: "expired" };

  // A QRIS buyer leaves to their banking app and comes back. Polling a
  // backgrounded tab is traffic nobody is looking at, and browsers throttle the
  // timers anyway.
  if (hidden) return { delayMs: null, stop: "hidden" };

  if (expiresAt === null && now - startedAt >= NO_EXPIRY_CAP_MS) {
    return { delayMs: null, stop: "gave-up" };
  }

  // Exponential backoff on consecutive errors: 4s, 8s, 16s… to a minute.
  // Errors here mean Altura or our own API is unwell, and hammering either is
  // how a wobble becomes an outage.
  const backoff = Math.min(
    BASE_INTERVAL_MS * 2 ** Math.max(0, consecutiveErrors),
    MAX_INTERVAL_MS,
  );

  // Never schedule a poll past the expiry. Landing exactly on it means the next
  // plan sees the dead QR and stops, rather than asking once more into the void.
  if (expiresAt !== null) {
    const remaining = expiresAt - now;
    if (backoff > remaining) return { delayMs: Math.max(MIN_INTERVAL_MS, remaining) };
  }

  return { delayMs: backoff };
}

/**
 * Parses an RFC 3339 expiry into epoch milliseconds.
 *
 * Returns null for anything unparseable rather than NaN, because NaN
 * comparisons are all false and a NaN expiry would silently disable every
 * expiry check in this file — the loop would run to the no-expiry cap instead
 * of stopping, and the page would never offer "start over".
 */
export function parseExpiry(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? null : ms;
}
